// Analítica del personalizador — emisor único del embed.
//
// UN solo módulo para los dos caminos (trigger/overlay V2 e inline-mode
// legacy). La versión anterior vivía dentro de inline-mode/, que está
// deprecado, así que el camino que usa de verdad el cliente no emitía
// NADA: el panel llevaba meses enseñando ceros. No vuelvas a copiar
// esto dentro de un flujo — misma lección que rescue-snapshot.ts.
//
// ── Por qué un singleton de módulo ──────────────────────────────────
// Los sitios que emiten eventos están repartidos por el wizard, el
// runtime, el cart-bridge y el editor de fotos. Enhebrar un `track` por
// toda esa cadena de llamadas habría tocado decenas de firmas. Como
// nunca hay dos personalizadores abiertos a la vez en una pestaña, un
// singleton es correcto aquí. `initTracker` lo ata al montaje y
// `disposeTracker` lo suelta al cerrar el overlay.
//
// ── Lotes, no un POST por evento ────────────────────────────────────
// Un cliente teclea cientos de veces en una sesión. Los eventos se
// encolan y se vacían cada FLUSH_MS (o al llegar a MAX_QUEUE), y los
// `field_changed` se COALESCEN por campo: de cien pulsaciones en el
// campo "nombre" sale un evento por ventana de vaciado con el último
// valor. Sin eso, la tabla crecería a razón de una fila por tecla.
//
// ── Privacidad ──────────────────────────────────────────────────────
//   · sessionId: cadena aleatoria en sessionStorage, muere al cerrar la
//     pestaña. No es un id de usuario ni cruza visitas.
//   · device: se bucketiza AQUÍ (mobile|tablet|desktop) para que el
//     servidor no vea nunca un user-agent.
//   · Sin cookies, sin IP, sin SDK de terceros.
//   · Del origen de la visita sale sólo el HOST del referente y los
//     parámetros utm_*. Nunca la URL de referencia entera —puede llevar
//     lo que el cliente buscó— ni nada que identifique a una persona.
//   · El valor tecleado solo viaja si el comercio activó
//     `captureFieldValues`. El servidor lo vuelve a comprobar — este
//     gate es para no mandar dato personal por la red en vano, no es
//     la garantía.
//
// Interruptores del comercio (desde /admin/personalizer):
//   · enableAnalytics === false → no-op total.
//   · gaMeasurementId + window.gtag → espeja cada evento a GA4.
//   · webhooks[] → POST saliente a la URL que case con el evento.

import { getCurrentSettings } from '../personalizer-settings/index.js';

export type EventKind =
  | 'trigger_shown'
  | 'viewed'
  | 'step_viewed'
  | 'step_blocked'
  | 'field_started'
  | 'field_changed'
  | 'photo_uploaded'
  | 'preset_changed'
  | 'map_moved'
  | 'ideas_used'
  | 'variant_changed'
  | 'tips_opened'
  | 'save_opened'
  | 'design_saved'
  | 'added_to_cart'
  | 'closed'
  | 'abandoned'
  | 'error';

export type EventDetail = {
  stepId?: string;
  stepIndex?: number;
  fieldId?: string;
  fieldLabel?: string;
  /** Lo que escribió el cliente. Se descarta salvo opt-in del comercio. */
  value?: string;
  payload?: Record<string, unknown>;
};

type Queued = EventDetail & {
  kind: EventKind;
  at: number;
  /** Valor de `openedAt` en el instante de encolar; 0 = el
   *  personalizador aún no estaba abierto.
   *
   *  Se congela aquí en vez de compararse contra el `openedAt` actual al
   *  vaciar, porque comparar marcas de tiempo no distingue un evento
   *  anterior a la apertura de uno simultáneo: `trigger_shown` y la
   *  apertura caen en el mismo milisegundo cuando el cliente pulsa el
   *  botón nada más verlo, y ese caso se pintaba en el panel como si la
   *  impresión hubiera ocurrido ya dentro del personalizador. */
  openedAtOnQueue: number;
};

type Ctx = {
  shop: string;
  apiBase: string;
  templateId: string;
  productHandle?: string;
};

/** De dónde llegó esta visita. Se resuelve UNA vez por pestaña y se
 *  guarda en sessionStorage — ver `origenDeLaVisita`. */
export type Origen = {
  /** Host del referente, `interno` si venía de la propia tienda, vacío
   *  si no había referente (acceso directo o marcador). NUNCA la URL:
   *  puede llevar la búsqueda del cliente o un id de sesión ajeno. */
  referrerHost?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
};

const SESSION_STORAGE_KEY = '__dfp_event_session';
/** La VISITA: id en localStorage con caducidad deslizante. Ver
 *  `visitId()` para por qué no puede ser el de la pestaña. */
const VISIT_STORAGE_KEY = '__dfp_visit';
/** Una visita se acaba tras media hora sin señales. Es la definición de
 *  Shopify y la de cualquier herramienta de analítica, y usar la misma
 *  es lo que permite comparar nuestras cifras con las suyas. */
const VISITA_MS = 30 * 60_000;
/** El origen de la visita, congelado al entrar. Clave aparte del
 *  sessionId para que un fallo leyendo una no se lleve la otra. */
const ORIGIN_STORAGE_KEY = '__dfp_event_origin';
/** Ventana de vaciado. 4 s es suficientemente corto para que un cierre
 *  brusco pierda poco y suficientemente largo para coalescer el tecleo
 *  de un campo entero en un evento. */
const FLUSH_MS = 4_000;
/** Vaciado anticipado si la cola se llena antes de que venza el timer. */
const MAX_QUEUE = 20;
/** Tope de la cola en el peor caso (pestaña sin red). Por encima se
 *  tiran los MÁS VIEJOS: en una sesión larga interesa más el final
 *  (dónde se atascó, si compró) que el principio. */
const MAX_QUEUE_HARD = 200;
/** Techo de `dtMs` que acepta el servidor (ver storefront/events.ts). */
const MAX_DT_MS = 6 * 3_600_000;
/** Content-type del envío. Uno de los tres CORS-safelisted, elegido
 *  para que la petición NO dispare preflight — ver flushTracker. */
const BEACON_CONTENT_TYPE = 'text/plain;charset=UTF-8';

let ctx: Ctx | null = null;
let sid = '';
let device: 'mobile' | 'tablet' | 'desktop' = 'desktop';
let locale: string | undefined;
/** Instante en que se abrió el personalizador. 0 = aún no abierto; los
 *  eventos previos (trigger_shown) van sin `msSinceOpen`. */
let openedAt = 0;
let origen: Origen = {};
let queue: Queued[] = [];
/** Coalescencia de `field_changed`: fieldId → último evento pendiente. */
let pendingFields = new Map<string, Queued>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let listenersBound = false;
/** Campos que ya dispararon `field_started` en esta sesión. */
const startedFields = new Set<string>();

export function sessionId(): string {
  try {
    const existing = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (existing && existing.length >= 8) return existing;
    // 24 chars base36: sobra para evitar colisiones por tienda y sesión,
    // y es lo bastante opaco como para no invitar a minar patrones.
    const id = (
      Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2)
    ).slice(0, 24);
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, id);
    return id;
  } catch {
    // sessionStorage bloqueado (Safari privado, iframes de terceros).
    // Un id por llamada degrada el recuento de sesiones únicas, pero
    // los agregados siguen sirviendo.
    return Math.random().toString(36).slice(2, 26);
  }
}

/** El id de la VISITA, no el de la pestaña.
 *
 *  Vive en localStorage con caducidad deslizante de media hora, y esa
 *  es la diferencia que lo hace correcto: `sessionId()` muere con la
 *  pestaña, así que un cliente que abre dos fichas en pestañas nuevas
 *  —o que navega dentro del navegador de Instagram, que a veces no
 *  conserva `sessionStorage` entre saltos— se contaba como dos, tres o
 *  cuatro visitas distintas.
 *
 *  Medido en producción el 14-sep-2026 contra las cifras de Shopify:
 *  las PÁGINAS cuadraban (1.932 nuestras contra ~1.731 suyas al día)
 *  pero las sesiones no —985 nuestras en medio día contra 757 suyas en
 *  el día entero—, y nuestras páginas por visita salían 1,73 contra sus
 *  2,07. O sea: las mismas páginas repartidas en más visitas. El
 *  recuento de visitas es el denominador de CASI TODO el panel, así que
 *  el error se propagaba a cada porcentaje.
 *
 *  Sigue sin cruzar visitas: media hora de silencio y el id se tira, no
 *  hay identificador permanente de persona y no se sabe si alguien
 *  vuelve mañana. */
export function visitId(): string {
  const ahora = Date.now();
  try {
    const crudo = window.localStorage.getItem(VISIT_STORAGE_KEY);
    if (crudo) {
      const v = JSON.parse(crudo) as { id?: unknown; ts?: unknown };
      if (typeof v.id === 'string' && v.id.length >= 8
        && typeof v.ts === 'number' && ahora - v.ts < VISITA_MS) {
        // Deslizante: cada señal alarga la visita media hora más.
        window.localStorage.setItem(VISIT_STORAGE_KEY, JSON.stringify({ id: v.id, ts: ahora }));
        return v.id;
      }
    }
  } catch { /* localStorage bloqueado: se cae al id por llamada. */ }

  const id = (
    Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2)
  ).slice(0, 24);
  try {
    window.localStorage.setItem(VISIT_STORAGE_KEY, JSON.stringify({ id, ts: ahora }));
  } catch {
    // Sin almacén, cada página sería una visita — que es justo lo que
    // esto viene a arreglar. Se cae al id de la PESTAÑA, que al menos
    // agrupa lo que pase dentro de ella.
    return sessionId();
  }
  return id;
}

/** De dónde viene la visita, resuelta una sola vez por pestaña.
 *
 *  Se congela en sessionStorage en el PRIMER personalizador que se
 *  monta y se reutiliza después. Hace falta porque lo que se mide aquí
 *  es el último salto: en cuanto el cliente navega de una ficha a otra
 *  dentro de la tienda, `document.referrer` pasa a ser la tienda misma
 *  y el «vino de Instagram» se pierde. Congelándolo, el origen de la
 *  segunda ficha sigue siendo el de la entrada — que es la pregunta que
 *  el comercio hace.
 *
 *  Privacidad: del referente sólo sale el HOST. La URL completa puede
 *  llevar lo que el cliente buscó o un id de sesión de otro sitio, y
 *  eso sí sería dato personal; el host no identifica a nadie. */
export function origenDeLaVisita(): Origen {
  try {
    const guardado = window.sessionStorage.getItem(ORIGIN_STORAGE_KEY);
    if (guardado) return JSON.parse(guardado) as Origen;
  } catch { /* sessionStorage bloqueado: se recalcula en cada montaje. */ }

  const o: Origen = {};
  try {
    const ref = document.referrer;
    if (ref) {
      const host = new URL(ref).host;
      // Centinela para la navegación interna. No es un host válido, así
      // que no puede colisionar con uno real (ver clasificarOrigen en
      // la API, que es quien le pone nombre).
      o.referrerHost = host === window.location.host ? 'interno' : host;
    } else {
      // SIN referente es un dato, no la ausencia de uno: es una visita
      // directa —marcador, la app de Instagram, el nombre tecleado a
      // mano—. Se manda la cadena VACÍA, que es lo que el panel lee como
      // «Directo»; dejarlo sin poner lo guardaría como nulo, que
      // significa «esto no se midió» y se pinta «Sin datos».
      //
      // Estuvo mal un fin de semana: medido en producción el 13-sep-2026,
      // 180 sesiones de tres días con nulo y NINGUNA con cadena vacía, o
      // sea todo el tráfico directo escondido detrás de «Sin datos».
      o.referrerHost = '';
    }
  } catch {
    // Referente ilegible (about:, data:). Aquí sí se deja sin poner: no
    // sabemos de dónde vino, y llamarlo directo sería inventárselo.
  }

  try {
    const q = new URLSearchParams(window.location.search);
    const corto = (v: string | null): string | undefined =>
      v ? v.slice(0, 80) : undefined;
    o.utmSource = corto(q.get('utm_source'));
    o.utmMedium = corto(q.get('utm_medium'));
    o.utmCampaign = corto(q.get('utm_campaign'));
    // El identificador de clic de GOOGLE, y sólo ese, rellena el hueco
    // cuando el anuncio no traía utm_source —que es lo normal en
    // Performance Max—. Lo pone el autoetiquetado de Google Ads, que no
    // toca los resultados de búsqueda normales: si está, el clic se
    // pagó.
    //
    // `fbclid` NO sirve para esto y estuvo aquí un día: Meta lo añade a
    // CUALQUIER enlace saliente que se pulse en Facebook o Instagram,
    // anuncio o publicación normal. Deducir «de pago» de él marcaría
    // como publicidad a todo el tráfico social de la tienda, que es
    // mentir en la única columna que existe para no mentir. Lo mismo
    // vale para `ttclid`, que tampoco se puede dar por exclusivo de un
    // anuncio. Los anuncios de Meta se etiquetan en el campo «URL
    // parameters» del propio anuncio (ver CLAUDE.md).
    if (!o.utmSource && (q.has('gclid') || q.has('gbraid') || q.has('wbraid'))) {
      o.utmSource = 'google';
      o.utmMedium = o.utmMedium ?? 'cpc';
    }
  } catch { /* sin query legible. */ }

  try {
    window.sessionStorage.setItem(ORIGIN_STORAGE_KEY, JSON.stringify(o));
  } catch { /* nada que hacer: el origen se recalcula. */ }
  return o;
}

export function detectDevice(): 'mobile' | 'tablet' | 'desktop' {
  try {
    const w = window.innerWidth || 0;
    // Umbrales ajustables por el comercio. Defaults: <600 / <1024.
    const b = getCurrentSettings()?.deviceBuckets;
    const mobileMax = Math.max(200, Math.min(4000, Number(b?.mobileMax) || 600));
    const tabletMax = Math.max(mobileMax + 50, Math.min(4000, Number(b?.tabletMax) || 1024));
    if (w < mobileMax) return 'mobile';
    if (w < tabletMax) return 'tablet';
    return 'desktop';
  } catch {
    return 'desktop';
  }
}

/** Vista previa del panel (preview.html marca el host con
 *  `data-dfp-preview`). Lo que el comercio prueba ahí no es una visita:
 *  antes salía en Analítica como «Móvil, entró directo · 2 s en …»,
 *  y en una tienda recién instalada era la ÚNICA visita. */
function enVistaPreviaDelPanel(): boolean {
  try {
    return !!document.querySelector('.dfp-personalizer[data-dfp-preview], #dfp-personalizer[data-dfp-preview]');
  } catch {
    return false;
  }
}

/** ¿Lo conduce un programa?
 *
 *  `navigator.webdriver` lo pone el navegador cuando lo lleva Selenium,
 *  Playwright o un rastreador que ejecute JS. `pageview.ts` lo
 *  descartaba desde el principio y ESTO no, así que teníamos la mitad
 *  de la medida limpia y la otra no: cada auditoría que corríamos
 *  nosotros mismos contra la tienda se contaba como un cliente que
 *  abrió el personalizador y no compró. Medido en producción el
 *  10-oct-2026: 1.780 de 12.103 sesiones del mes —el 14,7 %— no tenían
 *  visita detrás, y arrastraban las cifras a lo tonto (abrieron el
 *  48,7 % contra el 43,2 % real, móvil el 67 % contra el 70 %, y un 27 %
 *  de las filas de «Campos que bloquean» eran suyas).
 *
 *  El panel ya no las cuenta —las descarta al leer, que es lo que
 *  arregla el histórico— pero el sitio donde esto se corta de verdad es
 *  aquí: lo que no se emite no hay que filtrarlo después.
 *
 *  No caza a todos los robots, sólo a los que se declaran. Es el mismo
 *  trato que en `pageview.ts`, y por eso el filtro del panel se queda
 *  igualmente: son dos redes, no una repetida. */
function conducidoPorUnPrograma(): boolean {
  try {
    return navigator.webdriver === true;
  } catch {
    return false;
  }
}

function analyticsOff(): boolean {
  return getCurrentSettings()?.enableAnalytics === false
    || enVistaPreviaDelPanel()
    || conducidoPorUnPrograma();
}

function capturesValues(): boolean {
  return getCurrentSettings()?.captureFieldValues === true;
}

/** Ata el emisor a un montaje. Idempotente para el mismo templateId,
 *  así que llamarlo dos veces (CTA + apertura del overlay) no reinicia
 *  la sesión ni pierde la cola. */
export function initTracker(opts: Ctx): void {
  if (!opts.templateId) return;
  if (ctx && ctx.templateId === opts.templateId) {
    // Solo refresca el contexto que puede llegar tarde (el handle del
    // producto lo resuelve el bridge después del primer render).
    if (opts.productHandle) ctx.productHandle = opts.productHandle;
    return;
  }
  // Montaje de OTRA plantilla: el estado por-plantilla no puede
  // sobrevivir. Si `startedFields` persistiera, el primer toque de un
  // campo homónimo en la plantilla nueva no emitiría `field_started`; e
  // `interacted` heredado abriría la compuerta del diff antes de que el
  // cliente tocara nada, que es justo lo que esa compuerta evita.
  ctx = { ...opts };
  startedFields.clear();
  interacted = false;
  sid = sessionId();
  origen = origenDeLaVisita();
  device = detectDevice();
  try { locale = navigator.language || undefined; } catch { locale = undefined; }
  bindUnloadFlush();
}

/** Marca el t0 del que cuelgan todos los `msSinceOpen`. Lo llama el
 *  overlay al abrirse; sin esto los tiempos del panel salen a cero. */
export function markCustomizerOpen(): void {
  if (openedAt === 0) openedAt = Date.now();
}

/** Id de la sesión analítica en curso, o null si no hay contexto.
 *
 *  Lo usa cart-bridge para estampar `_dfp_analytics_session` en el line
 *  item: es lo que permite al webhook de pedidos cerrar el embudo y
 *  distinguir un carrito pagado de uno abandonado. Sigue sin ser un id
 *  de usuario — pero ATARLO a un pedido sí lo vuelve dato personal, así
 *  que customers/redact lo borra (ver gdpr.ts). */
export function analyticsSessionId(): string | null {
  return ctx ? sid : null;
}

/** ¿Cuánto lleva el cliente dentro? `undefined` si aún no ha abierto. */
export function msSinceOpen(): number | undefined {
  return openedAt > 0 ? Date.now() - openedAt : undefined;
}

/** El cliente ha tocado algo de verdad (puntero o teclado).
 *
 *  Existe porque el personalizador ESCRIBE en el formulario al abrirse:
 *  siembra los valores por defecto de la plantilla y restaura el
 *  borrador guardado. Esas escrituras pasan por el mismo punto de
 *  cambio que las del cliente, así que el diff las veía como ediciones
 *  y emitía una ráfaga de `field_changed` a los ~30 ms de abrir. El
 *  efecto en el panel era demoledor y silencioso: "tocaron el diseño"
 *  salía al 100% en todas las sesiones y la etapa dejaba de distinguir
 *  a quien se puso a diseñar de quien miró y cerró.
 *
 *  Un gesto real siempre precede a una edición real, así que esta
 *  compuerta no pierde nada del cliente y descarta todo lo del sistema. */
/** La rama del test A/B que le tocó a este visitante, ya etiquetada
 *  (`<experimento>:<rama>`). Null mientras no haya un experimento vivo,
 *  que es lo normal. */
let experimento: string | null = null;

/**
 * Apunta en qué rama de un test A/B cayó este visitante.
 *
 * SE LLAMA CUANDO LA RAMA YA HA PODIDO VERSE, no al abrir. La diferencia
 * decide qué mide el panel: el precio del botón sólo aparece en el paso
 * «Formato», así que registrando al abrir, el denominador incluiría a
 * todo el que se fue antes de llegar —gente que vio exactamente lo mismo
 * en las dos ramas— y el efecto quedaría diluido en el ruido de los que
 * ni miraron. Registrando al llegar, las dos ramas se comparan sobre la
 * población que sí vio la diferencia.
 *
 * Y no sesga: llegar a «Formato» ocurre ANTES de que las dos ramas se
 * separen, así que no puede depender de la rama.
 *
 * Viaja en los lotes siguientes; el servidor se queda con el primero que
 * llegue (COALESCE), como el origen de la visita.
 */
export function marcarExperimento(etiqueta: string): void {
  if (experimento === null) experimento = etiqueta;
}

let interacted = false;
export function markUserInteraction(): void { interacted = true; }
export function userHasInteracted(): boolean { return interacted; }

/** Emite un evento. No-op si no hay contexto o si el comercio apagó la
 *  analítica — los sitios de llamada no necesitan protegerse. */
export function track(kind: EventKind, detail: EventDetail = {}): void {
  if (!ctx || analyticsOff()) return;

  // `field_started` se emite una vez por campo y por sesión; el resto
  // de toques del mismo campo son `field_changed`.
  if (kind === 'field_changed' && detail.fieldId) {
    if (!startedFields.has(detail.fieldId)) {
      startedFields.add(detail.fieldId);
      enqueue({ ...detail, kind: 'field_started', at: Date.now(), openedAtOnQueue: openedAt });
    }
    // Coalescencia: el último valor del campo gana dentro de la ventana.
    pendingFields.set(detail.fieldId, {
      ...detail, kind, at: Date.now(), openedAtOnQueue: openedAt,
    });
    scheduleFlush();
    return;
  }

  enqueue({ ...detail, kind, at: Date.now(), openedAtOnQueue: openedAt });
}

function enqueue(e: Queued): void {
  mirrorToGa4(e);
  fanOutWebhooks(e);
  queue.push(e);
  if (queue.length > MAX_QUEUE_HARD) queue = queue.slice(-MAX_QUEUE_HARD);
  if (queue.length + pendingFields.size >= MAX_QUEUE) flushTracker();
  else scheduleFlush();
}

function scheduleFlush(): void {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flushTracker();
  }, FLUSH_MS);
}

/** Vacía la cola ya. Llámalo antes de cualquier navegación que se lleve
 *  la página por delante (el redirect de add-to-cart, sobre todo). */
export function flushTracker(): void {
  if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }
  if (!ctx) return;

  const events = [...queue, ...pendingFields.values()].sort((a, b) => a.at - b.at);
  queue = [];
  pendingFields = new Map();
  if (events.length === 0) return;

  const keepValues = capturesValues();
  const now = Date.now();
  // El servidor tolera 60 por lote; si hay más, se manda el tramo más
  // reciente (ver MAX_QUEUE_HARD para el porqué de quedarse con el final).
  const slice = events.slice(-60);
  const body = JSON.stringify({
    templateId: ctx.templateId,
    sessionId: sid,
    locale,
    device,
    productHandle: ctx.productHandle,
    // El id de la VISITA viaja con los eventos para poder cruzar lo que
    // pasa dentro del personalizador con las páginas que vio antes. La
    // clave de la sesión del personalizador sigue siendo la pestaña: es
    // donde vive su borrador y su diseño.
    visitId: visitId(),
    // El origen viaja en CADA lote y no sólo en el primero: el servidor
    // se queda con el primero que llega (COALESCE), y un lote perdido
    // —que es el caso normal con sendBeacon— dejaría la sesión sin
    // origen para siempre.
    referrerHost: origen.referrerHost,
    utmSource: origen.utmSource,
    utmMedium: origen.utmMedium,
    utmCampaign: origen.utmCampaign,
    // La rama del test A/B, si este visitante llegó a estar en uno. Va
    // en cada lote por lo mismo que el origen: el servidor se queda con
    // el primero y un lote perdido dejaría la sesión sin rama, o sea
    // fuera de la comparación.
    experiment: experimento ?? undefined,
    events: slice.map((e) => ({
      kind: e.kind,
      // Desfase entre el instante real y este vaciado, para que el
      // servidor reconstruya el `createdAt` correcto en vez de aplastar
      // todo el lote en el mismo milisegundo.
      //
      // Recortado al mismo techo que valida el servidor (6 h): un evento
      // que superara el límite se caía del lote, y el candidato natural
      // a llevar mucho en cola es el más antiguo — o sea `trigger_shown`
      // y `viewed`, los denominadores del embudo. Perder precisión en un
      // caso patológico es mejor que perder el evento.
      dtMs: Math.min(MAX_DT_MS, Math.max(0, now - e.at)),
      stepId: e.stepId,
      stepIndex: e.stepIndex,
      fieldId: e.fieldId,
      fieldLabel: e.fieldLabel,
      value: keepValues ? e.value : undefined,
      // `undefined` —no 0— para lo ocurrido ANTES de abrir, decidido con
      // el estado congelado al encolar (ver `openedAtOnQueue`).
      msSinceOpen: e.openedAtOnQueue > 0
        ? Math.max(0, e.at - e.openedAtOnQueue)
        : undefined,
      payload: e.payload,
    })),
  });

  const url = `${ctx.apiBase}/storefront/events?shop=${encodeURIComponent(ctx.shop)}`;
  try {
    // sendBeacon es la primitiva correcta: la petición se encola aunque
    // la página se esté descargando (add-to-cart → redirect de Shopify,
    // cierre de pestaña).
    //
    // ⚠️ El tipo del Blob TIENE que ser text/plain, no application/json.
    //
    // El storefront (dondefue.com) y la API (personalizador.brandeate.es)
    // son orígenes distintos. `application/json` no es un content-type
    // CORS-safelisted, así que obliga a un preflight — y un beacon con
    // preflight se pierde: el navegador manda el OPTIONS, recibe su 204
    // correcto, y NO envía nunca el POST. `sendBeacon` devuelve `true`
    // igualmente (solo dice "encolado"), no hay excepción y no hay error
    // en consola. Medido en producción el 22-ago-2026: meses de OPTIONS
    // cada 4 s sin un solo POST, y cero filas en la tabla.
    //
    // text/plain SÍ está en la lista blanca, así que la petición sale
    // directa y sin handshake. El cuerpo sigue siendo JSON; el servidor
    // lo parsea con un content-type parser propio (ver events.ts).
    //
    // Verificarlo en local NO sirve: el dev server del admin proxya
    // /storefront en el MISMO origen, así que ahí no hay preflight que
    // fallar. Este camino solo se puede comprobar cross-origin.
    const blob = new Blob([body], { type: BEACON_CONTENT_TYPE });
    const ok = typeof navigator.sendBeacon === 'function'
      ? navigator.sendBeacon(url, blob)
      : false;
    if (!ok) {
      void fetch(url, {
        method: 'POST',
        // Mismo tipo que el beacon y por el mismo motivo: así el
        // fallback tampoco paga un preflight.
        headers: { 'Content-Type': BEACON_CONTENT_TYPE },
        body,
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
    // La analítica jamás propaga hacia arriba.
  }
}

/** Vaciado al ocultar/descargar la página. `visibilitychange` es el que
 *  de verdad funciona en iOS Safari, donde `beforeunload` no dispara
 *  al cambiar de app; `pagehide` cubre el bfcache. */
function bindUnloadFlush(): void {
  if (listenersBound || typeof document === 'undefined') return;
  listenersBound = true;
  const onHide = (): void => {
    if (document.visibilityState === 'hidden') flushTracker();
  };
  document.addEventListener('visibilitychange', onHide);
  window.addEventListener('pagehide', flushTracker);
}

/** Suelta el contexto al cerrar el personalizador, vaciando antes lo que
 *  quede. NO borra el sessionId: si el cliente vuelve a abrir en la
 *  misma pestaña sigue siendo la misma visita, y contarla dos veces
 *  inflaría el numerador de "gente que entra". */
export function disposeTracker(): void {
  flushTracker();
  ctx = null;
  openedAt = 0;
  interacted = false;
  // Se vacía DESPUÉS del `flushTracker` de arriba, así que el último
  // lote sale con su rama. La fuente de verdad es el almacén del
  // navegador: si el cliente reabre, `marcarExperimento` la vuelve a
  // poner con el mismo valor.
  experimento = null;
  startedFields.clear();
}

// ── Espejo a GA4 ──────────────────────────────────────────────────────
// Independiente del endpoint interno: si el comercio ya vive en GA4,
// ve los mismos eventos en su panel de siempre. Best-effort — si gtag
// aún no ha cargado, se salta en silencio.
function mirrorToGa4(e: Queued): void {
  const prs = getCurrentSettings();
  if (!prs?.gaMeasurementId || !ctx) return;
  try {
    const gtag = (window as unknown as { gtag?: (...args: unknown[]) => void }).gtag;
    if (typeof gtag !== 'function') return;
    const prefix = prs.analyticsEventPrefix ?? '';
    // Nombres de evento GA4: minúsculas, [a-z0-9_], máx. 40 chars.
    const eventName = `${prefix}${e.kind}`.slice(0, 40);
    gtag('event', eventName, {
      send_to: prs.gaMeasurementId,
      ...(e.payload ?? {}),
      ds_template_id: ctx.templateId,
      ds_session_id: sid,
      ds_device: device,
      ...(e.stepId ? { ds_step_id: e.stepId } : {}),
      ...(e.fieldId ? { ds_field_id: e.fieldId } : {}),
    });
  } catch {
    // Un fallo del espejo nunca puede romper el flujo del cliente.
  }
}

// ── Webhooks salientes del comercio ───────────────────────────────────
// Solo POSTea a las URLs cuyo `event` case EXACTAMENTE con el kind y que
// no estén desactivadas. keepalive + no-cors para que un webhook lento
// no bloquee al cliente; los fallos se tragan.
function fanOutWebhooks(e: Queued): void {
  const prs = getCurrentSettings();
  const webhooks = Array.isArray(prs?.webhooks) ? prs.webhooks : [];
  if (webhooks.length === 0 || !ctx) return;
  for (const w of webhooks) {
    if (!w || w.enabled === false || w.event !== e.kind) continue;
    const target = w.url;
    if (typeof target !== 'string' || !/^https?:\/\//.test(target)) continue;
    try {
      void fetch(target, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shop: ctx.shop,
          templateId: ctx.templateId,
          kind: e.kind,
          payload: e.payload,
          stepId: e.stepId,
          fieldId: e.fieldId,
          sessionId: sid,
          locale,
          device,
          ts: e.at,
        }),
        keepalive: true,
        mode: 'no-cors',  // las URLs del comercio rara vez ponen CORS
      }).catch(() => {});
    } catch { /* nunca propaga */ }
  }
}
